/** The websocket owner decodes large saved envelopes off-thread. Request-only
 * consumers with no page runtime retain the native JSON transport reader. */
export type SavedReadResponse = Pick<Response, 'ok' | 'status' | 'statusText' | 'headers' | 'json'> & Partial<Pick<Response, 'clone'>>;
/** Retry explicit temporary read failures twice; final failures retain the caller's error UI. */
export async function fetchSavedResponse(input: string, init?: RequestInit): Promise<SavedReadResponse> {
    const read = (globalThis as { ftFetchSavedResponse?: (input: string, init?: RequestInit) => Promise<SavedReadResponse> }).ftFetchSavedResponse;
    for (let attempt = 0; ; attempt++) {
        init?.signal?.throwIfAborted();
        const response = await (read ? read(input, init) : fetch(input, init));
        if (response.status !== 503 || attempt === 2) return response;
        const body: unknown = await (response.clone?.() ?? response).json().catch(() => null);
        // Worker reads already hold decoded JSON; preserve it for callers without a native clone.
        if (!response.clone) response.json = async () => body;
        if (!body || typeof body !== 'object' || (body as Record<string, unknown>).retryable !== true) return response;
        await new Promise(resolve => setTimeout(resolve, 150 * 2 ** attempt));
    }
}
export function readSavedResponse(response: Pick<Response, 'json'> & Partial<Pick<Response, 'text' | 'arrayBuffer'>>): Promise<unknown> {
    const decode = (globalThis as { ftDecodeSavedJson?: (json: string | ArrayBuffer) => Promise<unknown> }).ftDecodeSavedJson;
    if (decode && response.arrayBuffer) return response.arrayBuffer().then(decode);
    return decode && typeof response.text === 'function' ? response.text().then(decode) : response.json();
}
